<template>
  <div>
    <div class="flex items-baseline justify-between gap-2">
      <!-- Not titled: the tab holding this says what it is. What is worth
           saying here is which end of the list is the recent end. -->
      <span class="text-xs uppercase tracking-wide text-gray-400">
        Newest first
      </span>
      <button
        type="button"
        class="text-xs text-accent-secondary underline hover:text-dark-primary"
        title="Read it again, including anyone else's changes since you opened this"
        :disabled="loading"
        @click="load"
      >
        {{ loading ? 'Reading…' : 'Refresh' }}
      </button>
    </div>

    <p v-if="error" class="text-xs text-red-700 mt-1">{{ error }}</p>

    <p v-else-if="loading && !history" class="text-xs text-gray-400 mt-1">
      Reading the history…
    </p>

    <template v-else-if="history">
      <!-- The milestone callout. A milestone is the one kind of item whose
           whole point is a date, so "has it moved, and by how much" is
           answered above the log rather than left to be reconstructed from
           it. Amber and bordered, the same way an internal note is set
           apart on the triage screen: this is the same history, read with a
           different question in mind. -->
      <div
        v-if="history.is_milestone && history.date_summary.length"
        class="mt-1.5 border border-amber-300 bg-amber-50 rounded p-2.5 space-y-2"
      >
        <span class="block text-xs uppercase tracking-wide text-amber-900 font-semibold">
          Dates on this milestone
        </span>

        <!-- Where each date started, where it stands, and the drift between
             the two. The whole story in a line, before the moves one by
             one. -->
        <div
          v-for="row in history.date_summary"
          :key="`summary-${row.field}`"
          class="text-xs text-amber-900 flex flex-wrap items-baseline gap-x-1.5"
        >
          <span class="font-semibold">{{ row.label }}</span>
          <span class="text-amber-700">{{ renderValue(row.original) }}</span>
          <span class="text-amber-500">→</span>
          <span class="font-semibold tabular-nums">{{ renderValue(row.current) }}</span>
          <!-- "changes" rather than "moves": the first of them is usually
               the date being set rather than moved, and "moved 1 time" on a
               milestone that has only ever had one date is wrong. -->
          <span class="text-amber-700">
            · {{ row.moves }} change{{ row.moves === 1 ? '' : 's' }}
          </span>
          <span v-if="row.net_days !== null" :class="driftClass(row.net_days)">
            · {{ driftLabel(row.net_days) }} overall
          </span>
        </div>

        <ul class="space-y-1 pt-0.5 border-t border-amber-200">
          <li
            v-for="(change, index) in shownDateChanges"
            :key="`date-${index}`"
            class="text-xs text-amber-900 flex flex-wrap items-baseline gap-x-1.5"
          >
            <span class="text-amber-600 tabular-nums">
              {{ shortMoment(change.at) }}
            </span>
            <span class="text-amber-700">{{ change.by || 'unattributed' }}</span>
            <span class="text-amber-500">·</span>
            <span>{{ change.label }}</span>
            <span class="text-amber-700">{{ renderValue(change.from) }}</span>
            <span class="text-amber-500">→</span>
            <span class="font-semibold tabular-nums">{{ renderValue(change.to) }}</span>
            <span :class="movementClass(change)">{{ movementLabel(change) }}</span>
          </li>
        </ul>
        <button
          v-if="history.date_changes.length > PREVIEW"
          type="button"
          class="text-xs text-amber-800 underline hover:text-amber-900"
          @click="allDates = !allDates"
        >
          {{ allDates
            ? 'Show fewer'
            : `Show all ${history.date_changes.length} date changes` }}
        </button>
      </div>

      <!-- Said out loud rather than left as an absence. On a milestone the
           date is the question, so "it has not moved" is an answer worth
           printing -- a blank space where the callout would be reads as a
           panel that failed to load. -->
      <p
        v-else-if="history.is_milestone"
        class="mt-1.5 text-xs text-gray-500 border border-gray-200 rounded px-2 py-1.5"
      >
        This milestone's dates have not been changed since it was filed.
      </p>

      <p v-if="!history.revisions.length" class="text-xs text-gray-400 mt-1">
        Nothing recorded yet.
      </p>

      <!-- The general history, newest first. Every recorded change, whatever
           it touched -- including the dates above, which are not removed
           from here just because they are also called out. -->
      <ol v-else class="mt-1.5 space-y-2">
        <li
          v-for="revision in shownRevisions"
          :key="revision.history_id"
          class="text-xs border-l-2 border-gray-200 pl-2"
        >
          <div class="flex flex-wrap items-baseline gap-x-1.5 text-gray-500">
            <span class="tabular-nums">{{ shortMoment(revision.at) }}</span>
            <span class="text-gray-700">{{ revision.by || 'unattributed' }}</span>
            <span v-if="revision.type === '+'" class="text-gray-500">
              · created this item
            </span>
            <span v-else-if="revision.type === '-'" class="text-red-700">
              · deleted this item
            </span>
            <!-- Where the record starts, which is not always the create:
                 the items imported from Airtable were written in bulk and
                 so have no create snapshot at all. Said plainly, because
                 "nothing changed" on the oldest line reads as a bug. -->
            <span v-else-if="revision.earliest" class="text-gray-400">
              · earliest change on record
            </span>
            <span v-else-if="!revision.changes.length" class="text-gray-400">
              · saved with nothing changed
            </span>
          </div>

          <ul v-if="revision.changes.length" class="mt-0.5 space-y-0.5">
            <li
              v-for="change in revision.changes"
              :key="`${revision.history_id}-${change.field}`"
            >
              <!-- Prose is stacked rather than put on one line: a
                   description reading "a → b" with two paragraphs either
                   side of the arrow is unreadable. -->
              <template v-if="change.long">
                <span class="text-gray-500">{{ change.label }}</span>
                <span class="block text-gray-500 pl-2 line-through decoration-gray-300">
                  {{ change.from || '—' }}
                </span>
                <span class="block text-gray-900 pl-2">{{ change.to || '—' }}</span>
              </template>
              <!-- Spaced by the container rather than by the whitespace
                   between the tags: the template compiler condenses that
                   away, which ran the label into the value it labels. -->
              <span v-else class="flex flex-wrap items-baseline gap-x-1.5">
                <span class="text-gray-500">{{ change.label }}</span>
                <span class="text-gray-500">{{ renderValue(change.from) }}</span>
                <span class="text-gray-400">→</span>
                <span class="text-gray-900 font-medium">
                  {{ renderValue(change.to) }}
                </span>
              </span>
            </li>
          </ul>
        </li>
      </ol>

      <button
        v-if="history.revisions.length > PREVIEW"
        type="button"
        class="mt-1 text-xs text-accent-secondary underline hover:text-dark-primary"
        @click="allRevisions = !allRevisions"
      >
        {{ allRevisions
          ? 'Show the last few only'
          : `Show all ${history.revisions.length} revisions` }}
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed, defineProps, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { formatDateTime } from '@/composables/formatDateTime'
import { getWorkItemHistory } from '@/services/BackOffice'

// How much of each list is shown before asking. An item edited all year has
// a hundred revisions and none of them is why the form was opened; the last
// few answer "what just happened to this", which is the question.
const PREVIEW = 5

const props = defineProps({
  // Null while the form holds an item that does not exist yet. There is no
  // history of something that was never saved, so this draws nothing.
  itemId: { type: [Number, String], default: null },
})

const store = useStore()
const token = () => store.getters['auth/showToken']

const history = ref(null)
const loading = ref(false)
const error = ref('')
const allRevisions = ref(false)
const allDates = ref(false)

// Fetched here rather than handed down, because nobody else needs it: the
// board lists items without their history, and asking for it per row would
// be one request per card.
async function load() {
  if (!props.itemId) return
  loading.value = true
  error.value = ''
  try {
    history.value = await getWorkItemHistory(token(), props.itemId)
  } catch (e) {
    // The log is worth reading but nothing depends on it, so a failure
    // says so quietly and leaves the form working.
    error.value = 'The change log could not be read.'
  } finally {
    loading.value = false
  }
}

watch(() => props.itemId, id => {
  history.value = null
  allRevisions.value = false
  allDates.value = false
  error.value = ''
  if (id) load()
}, { immediate: true })

// Guarded although the template only renders these once `history` is set:
// a computed that throws on null takes the whole form down with it, and the
// form is the thing somebody came here to use.
const shownRevisions = computed(() => {
  const rows = history.value ? history.value.revisions : []
  return allRevisions.value ? rows : rows.slice(0, PREVIEW)
})

const shownDateChanges = computed(() => {
  const rows = history.value ? history.value.date_changes : []
  return allDates.value ? rows : rows.slice(0, PREVIEW)
})

// A stored value as it should read. Dates arrive as the server writes them
// and are turned around here rather than printed raw, and a blank -- which
// is how the server says "nothing" -- becomes a dash rather than a gap.
function renderValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  // A date, not a moment: split rather than passed to Date(), because
  // new Date('2027-01-15') is parsed as UTC midnight, which in Central is
  // the evening before -- every date would read a day early.
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (parts) return `${Number(parts[2])}/${Number(parts[3])}/${parts[1]}`
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value)
  return value
}

function shortMoment(value) {
  return value ? formatDateTime(value) : ''
}

function days(count) {
  const n = Math.abs(count)
  return `${n} day${n === 1 ? '' : 's'}`
}

// What one move did, in the fewest words that are still true. 'changed' is
// what a target quarter gets: it is stored as text in no enforced format,
// so it cannot be subtracted, only compared for difference.
function movementLabel(change) {
  if (change.movement === 'set') return '· date set'
  if (change.movement === 'cleared') return '· date cleared'
  if (change.days === null || change.days === undefined) return '· changed'
  return change.days > 0
    ? `· ${days(change.days)} later`
    : `· ${days(change.days)} earlier`
}

// Slipping is the thing anybody is looking for, so it is the thing that is
// coloured. Pulling a date in is good news and stays quiet.
function movementClass(change) {
  if (change.days > 0) return 'text-red-700 font-semibold'
  if (change.days < 0) return 'text-green-800'
  return 'text-amber-700'
}

function driftLabel(netDays) {
  if (netDays === 0) return 'back where it started'
  return netDays > 0 ? `${days(netDays)} later` : `${days(netDays)} earlier`
}

function driftClass(netDays) {
  if (netDays > 0) return 'text-red-700 font-semibold'
  if (netDays < 0) return 'text-green-800'
  return 'text-amber-700'
}
</script>
